import { Box } from "@mui/material"
import {
  type ColumnSort,
  type ExpandedState,
  type OnChangeFn,
  type SortingState,
  getCoreRowModel,
} from "@tanstack/react-table"
import type { FC } from "react"
import React, { useState } from "react"
import type { CombinedError } from "urql"

import EquivalentProductGroupExpandedRow from "../components/EquivalentProductGroupExpandedRow"
import { useEquivalentProductGroupsColumns } from "../hooks/useEquivalentProductGroupsColumns"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import type { EquivalencyGroup } from "@/graphql/codegen/graphql"

interface EquivalentProductGroupsTableProps {
  data: EquivalencyGroup[]
  fetching?: boolean
  error?: CombinedError | Error
  sorting: ColumnSort[]
  onHandleSortingChange: (updaterFn: (state: SortingState) => SortingState) => void
  onEdit?: (group: EquivalencyGroup) => void
  onDelete?: (equivalencyGroupId: string, groupName: string) => Promise<boolean>
}

const EquivalentProductGroupsTable: FC<EquivalentProductGroupsTableProps> = ({
  data,
  error,
  fetching = false,
  sorting,
  onHandleSortingChange,
  onEdit,
  onDelete,
}) => {
  const [expandedRow, setExpandedRow] = useState<ExpandedState>({})
  const columns = useEquivalentProductGroupsColumns({ onEdit, onDelete })

  const handleExpandedChange: OnChangeFn<ExpandedState> = (updater) => {
    setExpandedRow((old) => {
      const newExpanded = typeof updater === "function" ? updater(old) : updater

      // ExpandedState can be a boolean or an object, we only handle object case
      if (typeof newExpanded === "boolean") {
        return newExpanded
      }

      const expandedKeys = Object.keys(newExpanded).filter((key) => newExpanded[key])

      // If no rows expanded, return empty
      if (expandedKeys.length === 0) {
        return {}
      }

      // If multiple rows would be expanded, keep only the newly clicked one
      if (expandedKeys.length > 1) {
        const oldExpandedKey = typeof old === "object" ? Object.keys(old).find((key) => old[key]) : undefined
        const newKey = expandedKeys.find((key) => key !== oldExpandedKey)
        return { [newKey!]: true }
      }

      return newExpanded
    })
  }

  return (
    <Box className='flex h-full flex-col'>
      <AerosBaseTable
        error={error as Error | undefined}
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data,
            columns,
            state: {
              sorting,
              isLoading: fetching,
              expanded: expandedRow,
            },
            manualSorting: true,
            enableSorting: true,
            enableMultiSort: false,
            enableSortingRemoval: false,
            getCoreRowModel: getCoreRowModel(),
            onSortingChange: onHandleSortingChange as OnChangeFn<SortingState>,
            onExpandedChange: handleExpandedChange,
            getRowCanExpand: () => true,
            renderExpandedContent: (row) => <EquivalentProductGroupExpandedRow row={row} />,
          },
        }}
        slotProps={{
          bodyCellExpanded: { className: "bg-primary-100" },
          expandedCell: expandedCellStyles,
          bodyRow: {
            className: "hover:bg-primary-100 cursor-pointer",
          },
        }}
        noDataMessage='No equivalency groups found'
      />
    </Box>
  )
}

export default EquivalentProductGroupsTable
